import React, { useRef, useState, useEffect } from 'react';
import { Box } from '@mantine/core';
import { IconCubeSend, IconReplace } from '@tabler/icons-react';
import * as modelViewer from '@google/model-viewer';
import { useTranslation } from 'react-i18next';
import { Annotation } from '@/types/Annotation.model';
import { type ModelViewerPresentationSettings } from '@/constants/modelViewerPresentation';
import { ensureDefaultCameraInterpolation } from '@/utils/modelViewerZoom';
import { cycleModelViewerAnimation } from '@/utils/modelViewerAnimation';
import { isTypingKeyboardTarget } from '@/utils/keyboardShortcuts';
import { AnnotationNavigatorRef } from '@/components/Annotations';
import { IProduct } from '@/types/Product.model';
import { useAnnotationClickOutside } from '@/hooks/useAnnotationClickOutside';
import { useAnnotationClick } from '@/hooks/useAnnotationClick';
import { useAnnotationPositionUpdate } from '@/hooks/useAnnotationPositionUpdate';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { useSavedModelViewerCamera } from '@/hooks/useSavedModelViewerCamera';
import { useAnnotationPlacement } from '@/hooks/useAnnotationPlacement';
import { ModelViewerStage } from '@/components/ModelViewer/ModelViewerStage';
import { EditorChipButton } from '@/components/EditorChipButton';
import { useStore } from '@/store/Store';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';

interface ModelViewerProps {
  modelUrl: string;
  modelFile: File | null;
  showModelUploader: boolean;
  modelReplacement: boolean;
  initialCameraOrbit: string;
  /** Saved with preview — restores same pivot as product page */
  initialCameraTarget?: string;
  initialCameraFieldOfView?: string;
  modelViewerRef: React.RefObject<modelViewer.ModelViewerElement>;
  onModelFileChange: (file: File | null) => void;
  onShowModelUploaderChange: (show: boolean) => void;
  onModelReplacementChange: (replacement: boolean) => void;
  onExportModel: () => void;
  isEditing?: boolean;
  annotations?: Annotation[];
  isAnnotationMode?: boolean;
  onAnnotationAdd?: (
    position: string,
    normal: string,
    orbit: string,
    target: string,
    cameraFieldOfView: string
  ) => void;
  presentationSettings: ModelViewerPresentationSettings;
  onPresentationChange: (settings: ModelViewerPresentationSettings) => void;
  helpOpened?: boolean;
  onHelpOpenedChange?: (opened: boolean) => void;
}

export function ModelViewer({
  modelUrl,
  modelFile,
  showModelUploader,
  modelReplacement,
  initialCameraOrbit,
  initialCameraTarget = '',
  initialCameraFieldOfView = '',
  modelViewerRef,
  onModelFileChange,
  onShowModelUploaderChange,
  onModelReplacementChange,
  onExportModel,
  isEditing = false,
  annotations = [],
  isAnnotationMode = false,
  onAnnotationAdd,
  presentationSettings,
  onPresentationChange,
  helpOpened = false,
  onHelpOpenedChange,
}: ModelViewerProps) {
  const { t } = useTranslation();
  const color = useStore((state) => state.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;
  const [activeAnnotation, setActiveAnnotation] = useState<Annotation | null>(null);
  const [annotationsVisible, setAnnotationsVisible] = useState<boolean>(true);
  const [annotationPosition, setAnnotationPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [imageModalOpened, setImageModalOpened] = useState(false);
  const [productModalOpened, setProductModalOpened] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);
  const annotationNavigatorRef = useRef<AnnotationNavigatorRef>(null);
  const descriptionBoxRef = useRef<HTMLDivElement>(null);

  useModelViewerDynamicZoom(modelViewerRef, modelUrl);

  useEffect(() => {
    const mv = modelViewerRef.current;
    if (!mv || !modelUrl) return;
    ensureDefaultCameraInterpolation(mv);
  }, [modelUrl, modelViewerRef]);

  useSavedModelViewerCamera(modelViewerRef, modelUrl, {
    orbit: initialCameraOrbit,
    target: initialCameraTarget,
    fov: initialCameraFieldOfView,
  });

  useAnnotationPlacement({
    modelViewerRef,
    isAnnotationMode,
    modelUrl,
    onAnnotationAdd,
  });

  const closeAnnotationUi = () => {
    setActiveAnnotation(null);
    setAnnotationPosition(null);
    setImageModalOpened(false);
    setProductModalOpened(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    setActiveAnnotation(null);
    setAnnotationPosition(null);
    setImageModalOpened(false);
    setProductModalOpened(false);
    setSelectedProduct(null);
  }, [modelUrl]);

  useAnnotationClickOutside({
    descriptionBoxRef,
    activeAnnotation,
    onClose: closeAnnotationUi,
    imageModalOpened,
    productModalOpened,
  });

  const handleAnnotationClick = useAnnotationClick({
    modelViewerRef,
    annotationNavigatorRef,
    setActiveAnnotation,
    setAnnotationPosition,
    onCloseModals: () => {
      setImageModalOpened(false);
      setProductModalOpened(false);
      setSelectedProduct(null);
    },
  });

  useAnnotationPositionUpdate({
    modelViewerRef,
    activeAnnotation,
    setAnnotationPosition,
  });

  useEffect(() => {
    if (!modelUrl) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingKeyboardTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        if (isAnnotationMode || annotations.length === 0) return;
        event.preventDefault();
        if (event.key === 'ArrowRight') {
          annotationNavigatorRef.current?.navigateNext();
        } else {
          annotationNavigatorRef.current?.navigatePrevious();
        }
        return;
      }

      if (event.key === ']' || event.key === '[') {
        event.preventDefault();
        cycleModelViewerAnimation(modelViewerRef.current, event.key === ']' ? 1 : -1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modelUrl, isAnnotationMode, annotations.length, modelViewerRef]);

  return (
    <Box>
      <ModelViewerStage
        modelUrl={modelUrl}
        modelViewerRef={modelViewerRef}
        presentationSettings={presentationSettings}
        onPresentationChange={onPresentationChange}
        isAnnotationMode={isAnnotationMode}
        annotations={annotations}
        annotationsVisible={annotationsVisible}
        onAnnotationsVisibleChange={setAnnotationsVisible}
        onActiveAnnotationChange={setActiveAnnotation}
        activeAnnotation={activeAnnotation}
        annotationPosition={annotationPosition}
        handleAnnotationClick={handleAnnotationClick}
        annotationNavigatorRef={annotationNavigatorRef}
        descriptionBoxRef={descriptionBoxRef}
        imageModalOpened={imageModalOpened}
        onImageModalClose={() => setImageModalOpened(false)}
        productModalOpened={productModalOpened}
        onProductModalClose={() => {
          setProductModalOpened(false);
          setSelectedProduct(null);
        }}
        selectedProduct={selectedProduct}
        onProductClick={(product) => {
          setSelectedProduct(product);
          setProductModalOpened(true);
        }}
        onImageClick={() => setImageModalOpened(true)}
        initialCameraOrbit={initialCameraOrbit}
        initialCameraTarget={initialCameraTarget}
        initialCameraFieldOfView={initialCameraFieldOfView}
        chromeColor={color}
        helpOpened={helpOpened}
        onHelpOpenedChange={onHelpOpenedChange}
      />

      <Box
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '8px',
          marginTop: '4px',
        }}
      >
        <EditorChipButton
          type="button"
          accentColor={color}
          leftIcon={<IconCubeSend size={15} stroke={1.75} />}
          onClick={onExportModel}
          disabled={!modelFile && !(isEditing && modelUrl && !showModelUploader)}
        >
          {t('editProduct.generateStaticGLTF')}
        </EditorChipButton>

        {isEditing && !showModelUploader && (
          <EditorChipButton
            accentColor={color}
            leftIcon={<IconReplace size={15} stroke={1.75} />}
            onClick={() => onShowModelUploaderChange(true)}
          >
            {t('editProduct.replace3DModel')}
          </EditorChipButton>
        )}
      </Box>
    </Box>
  );
}
